/**
 * The decision panel on a credit ticket (subject `credit_hold`;
 * docs/features/credit-control/06_ui_strategy.md §2): the account's standing,
 * then every order waiting for a credit decision with Release · Proof only ·
 * Card or pro-forma (the payer's other methods) · Refuse on its own row, one shared note that each action asks
 * for when it is empty, and Release all. When nothing is left pending it says
 * "All decided" and lists the outcomes: the ticket is closed with a note.
 *
 * Managers and admins decide (the backend says which, `canManage`); everyone
 * else sees each order's status and no buttons. Self-loading by ticket id;
 * `onDecided` lets the ticket page refresh its status and activity, where each
 * new hold and each decision appears as a comment. Decisions post to the URL
 * the account's read carried; an order's own page is linked only when the
 * backend names one.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  BillingAccountView,
  CreditHoldsForTicket,
  HoldDecision,
  HoldView,
} from "@shared-core/data/credit-control-types";
import {
  appPathOrNull,
  HOLD_DECISION_ORDER,
  holdDecisionButtonLabel,
  holdMoment,
} from "@shared-core/domain/credit-control-presentation";
import { notifyCreditChanged } from "@shared-core/domain/creditEvents";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { paths } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Skeleton } from "../../primitives/skeleton";
import { ExposureStrip } from "./ExposureStrip";

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; data: CreditHoldsForTicket };

export function CreditHoldDecisionPanel({
  ticketId,
  onDecided,
}: {
  ticketId: string;
  onDecided?: () => void;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [note, setNote] = useState("");
  const [noteMissing, setNoteMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    adapter
      .getCreditHoldsForTicket(ticketId)
      .then((data) => {
        if (cancelled) return;
        setState(
          data
            ? { kind: "loaded", data }
            : { kind: "failed", message: "This ticket has no credit holds." },
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error
                ? err.message
                : "Could not load the held orders.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, ticketId]);

  if (state.kind === "loading") {
    return (
      <div className="mb-4 space-y-2" aria-label="Loading held orders">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (state.kind === "failed") {
    return (
      <Alert variant="destructive" role="alert" className="mb-4">
        <AlertDescription>
          Could not load the held orders: {state.message}
        </AlertDescription>
      </Alert>
    );
  }

  const { data } = state;
  const pending = data.holds.filter((h) => h.allowedDecisions.length > 0);

  const decide = async (decisions: { holdId: string; to: HoldDecision }[]) => {
    if (note.trim() === "") {
      setNoteMissing(true);
      noteRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    setNoteMissing(false);
    try {
      await adapter.resolveCreditHolds(data.billingAccount, {
        decisions,
        note: note.trim(),
      });
      setNote("");
      setState({
        kind: "loaded",
        data: (await adapter.getCreditHoldsForTicket(ticketId)) ?? data,
      });
      notifyCreditChanged();
      onDecided?.();
    } catch (err) {
      setError(mutationErrorMessage(err, "Could not record the decision."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby="credit-holds-heading"
      className="mb-4 space-y-3 rounded-lg border border-border p-4 text-sm"
    >
      <AccountStanding
        account={data.billingAccount}
        customerId={data.customerId}
      />
      <ExposureStrip
        exposure={data.exposure}
        unavailableNote={data.exposureUnavailableNote}
        variant="ticket"
        agedDebtSearch={data.billingAccount.ref}
        agedDebtNote={data.agedDebtNote}
      />
      {data.allDecided ? (
        <AllDecided data={data} />
      ) : (
        <div className="space-y-3">
          <h2 id="credit-holds-heading" className="font-medium">
            Orders waiting for a credit decision
          </h2>
          {error && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <ul className="divide-y divide-border rounded-md border border-border">
            {data.holds.map((hold) => (
              <HoldRow
                key={hold.id}
                hold={hold}
                otherMethodsLabel={data.billingAccount.otherMethodsLabel}
                canDecide={data.canManage}
                saving={saving}
                onDecide={(to) => void decide([{ holdId: hold.id, to }])}
              />
            ))}
          </ul>
          {data.canManage ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-56 flex-1 space-y-1">
                <Label htmlFor="credit-decision-note">Note (required)</Label>
                <Input
                  id="credit-decision-note"
                  ref={noteRef}
                  value={note}
                  aria-invalid={noteMissing}
                  aria-describedby={
                    noteMissing ? "credit-decision-note-error" : undefined
                  }
                  onChange={(e) => {
                    setNote(e.target.value);
                    if (e.target.value.trim() !== "") setNoteMissing(false);
                  }}
                />
                {noteMissing && (
                  <p
                    id="credit-decision-note-error"
                    className="text-xs text-destructive"
                  >
                    Add a note first: it is recorded with every decision.
                  </p>
                )}
              </div>
              <Button
                type="button"
                disabled={saving || pending.length === 0}
                onClick={() =>
                  void decide(
                    pending.map((h) => ({ holdId: h.id, to: "released" })),
                  )
                }
              >
                Release all
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Managers and admins decide on held orders.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/** "On stop · Overdue" (or "Active") · Credit account, 30 days from end of month · PO to follow", with a link to the payer's Settings. */
function AccountStanding({
  account,
  customerId,
}: {
  account: BillingAccountView;
  customerId: string | null;
}) {
  const parts = [
    account.onStop && account.stop
      ? `On stop · ${account.stop.reasonLabel}`
      : "Active",
    account.terms
      ? `Credit account, ${account.terms.displayLabel}`
      : account.methodsLabel,
    account.poRuleNote,
  ].filter((part): part is string => part !== null);
  return (
    <p className="flex flex-wrap items-baseline gap-x-3">
      <span className="font-medium">Account</span>
      <span
        className={
          account.onStop ? "font-medium text-amber-700 dark:text-amber-300" : ""
        }
      >
        {parts.join(" · ")}
      </span>
      {customerId && (
        <Link
          to={paths.customerSettings({ customerId })}
          className="text-primary hover:underline"
        >
          → Payment settings
        </Link>
      )}
    </p>
  );
}

function HoldRow({
  hold,
  otherMethodsLabel,
  canDecide,
  saving,
  onDecide,
}: {
  hold: HoldView;
  /** The payer's other methods, worded: names the release-without-credit button. */
  otherMethodsLabel: string | null;
  canDecide: boolean;
  saving: boolean;
  onDecide: (to: HoldDecision) => void;
}) {
  const decided = hold.status !== "held";
  return (
    <li className="space-y-1.5 p-3">
      <p>
        <HoldTitle hold={hold} />
        {" · "}
        {hold.subjectLabel}
        {hold.amountPence !== null &&
          ` · ${formatQuotedPrice(hold.amountPence)}`}
        {" · "}
        {hold.originLabel}
        {" · "}
        {holdMoment(hold.createdAt)}
      </p>
      {decided && hold.statusBy && (
        <p className="text-muted-foreground">
          {hold.statusLabel} since {holdMoment(hold.statusBy.at)}
          {hold.statusBy.name && ` (${hold.statusBy.name})`}
        </p>
      )}
      {!decided && !canDecide && (
        <p className="text-muted-foreground">{hold.statusLabel}</p>
      )}
      {canDecide && hold.allowedDecisions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {HOLD_DECISION_ORDER.filter((to) =>
            hold.allowedDecisions.includes(to),
          ).flatMap((to) => {
            // No button where there is nothing to offer: a payer with only
            // credit account has no way to pay instead.
            const label = holdDecisionButtonLabel(to, otherMethodsLabel);
            return label === null
              ? []
              : [
                  <Button
                    key={to}
                    type="button"
                    size="sm"
                    variant={to === "released" ? "default" : "outline"}
                    disabled={saving}
                    onClick={() => onDecide(to)}
                    aria-label={`${label} ${hold.subjectTitle}`}
                  >
                    {label}
                  </Button>,
                ];
          })}
        </div>
      )}
    </li>
  );
}

/** "Order ORD-K48193", linked to the subject's page when the backend names one. */
function HoldTitle({ hold }: { hold: HoldView }) {
  const to = appPathOrNull(hold.subjectUrl);
  return to ? (
    <Link to={to} className="font-medium text-primary hover:underline">
      {hold.subjectTitle}
    </Link>
  ) : (
    <span className="font-medium">{hold.subjectTitle}</span>
  );
}

function AllDecided({ data }: { data: CreditHoldsForTicket }) {
  return (
    <div className="space-y-2">
      <h2 id="credit-holds-heading" className="font-medium">
        All decided
      </h2>
      <p className="text-muted-foreground">
        Every order that waited on {data.billingAccount.name} has an outcome.
        The ticket is closed with a note.
      </p>
      <ul className="divide-y divide-border rounded-md border border-border">
        {data.holds.map((hold) => (
          <li key={hold.id} className="space-y-0.5 p-3">
            <p>
              <HoldTitle hold={hold} />
              {" · "}
              {hold.subjectLabel}
              {hold.amountPence !== null &&
                ` · ${formatQuotedPrice(hold.amountPence)}`}
            </p>
            <p className="text-muted-foreground">
              {hold.statusLabel}
              {hold.statusBy &&
                ` · ${hold.statusBy.name ? `${hold.statusBy.name} · ` : ""}${holdMoment(hold.statusBy.at)}`}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
